import { showToast } from '@eptai/eptai-app-components';
import axios, { AxiosError } from 'axios';

// Default timeout (ms) applied to every request made through apiClient.
// 60s is generous for slow networks while still catching a truly hung backend.
// Individual calls can override it (e.g. uploads pass timeout: 0 to opt out).
export const API_TIMEOUT = 60000;

// Stable id so concurrent timeout toasts (e.g. parallel requests) collapse to one.
export const API_TIMEOUT_TOAST_ID = 'api-request-timeout';

export const apiClient = axios.create({ timeout: API_TIMEOUT });

/**
 * True when axios aborted because the request exceeded its timeout (not a
 * caller AbortSignal cancel).
 *
 * Example: `{ code: 'ECONNABORTED', message: 'timeout of 60000ms exceeded' }` → true
 * Example: `{ code: 'ERR_CANCELED' }` → false
 */
export const isRequestTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const candidate = error as AxiosError;
  if (candidate.code !== 'ECONNABORTED') {
    return false;
  }
  // Abandoned requests should abort via AbortSignal (ERR_CANCELED). If the
  // signal is already aborted, skip the timeout toast for the stale attempt.
  if (candidate.config?.signal?.aborted) {
    return false;
  }
  return true;
};

/**
 * Show a single global timeout toast. Safe to call repeatedly — toastId
 * deduplicates while one is visible.
 */
export const notifyRequestTimeout = (): void => {
  showToast('error', 'The request took too long and timed out. Please try again.', {
    toastId: API_TIMEOUT_TOAST_ID,
  });
};

// Surface timeouts as a toast so the UI never sits in an indefinite spinner.
// Scoped to real request timeouts to avoid double-toasting HTTP errors and
// abandoned (AbortSignal) requests.
apiClient.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    if (isRequestTimeoutError(error)) {
      notifyRequestTimeout();
    }
    return Promise.reject(error);
  }
);
